import type { Language } from '../types'
import type { VoiceDiagnosticInput } from '../debug/VoiceDiagnosticStore'
import { VoiceInputError, type VoiceInputProvider } from './VoiceInputProvider'

const DEFAULT_STT_ENDPOINT = '/api/stt'
const DEFAULT_STT_BATCH_ID = '2026_busan_validation'
const DEFAULT_RECORDING_MS = 8_000
const MAX_CLIENT_AUDIO_BYTES = 10 * 1024 * 1024
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/webm',
  'audio/ogg;codecs=opus',
] as const

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
interface RecordedAudio {
  audio: Blob
  durationMs: number
}
type AudioRecorder = () => Promise<Blob | RecordedAudio>
type DiagnosticLogger = (event: VoiceDiagnosticInput) => void

interface SttResponse {
  transcript?: unknown
  code?: unknown
  debug?: unknown
}

export interface WhisperVoiceInputProviderOptions {
  endpoint?: string
  batchId?: string
  fetcher?: Fetcher
  recordAudio?: AudioRecorder
  supportCheck?: () => boolean
  debug?: boolean
  onDiagnostic?: DiagnosticLogger
}

function monotonicNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now()
}

function mediaRecorderConstructor(): typeof MediaRecorder | undefined {
  return typeof MediaRecorder === 'undefined' ? undefined : MediaRecorder
}

export function isMediaRecorderSupported(): boolean {
  return Boolean(
    mediaRecorderConstructor()
    && typeof navigator !== 'undefined'
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function',
  )
}

function preferredMimeType(Recorder: typeof MediaRecorder): string | undefined {
  if (typeof Recorder.isTypeSupported !== 'function') return undefined
  return PREFERRED_MIME_TYPES.find((mimeType) => Recorder.isTypeSupported(mimeType))
}

function fileExtensionForMimeType(mimeType: string): string {
  const normalized = mimeType.toLowerCase()
  if (normalized.includes('mp4')) return 'm4a'
  if (normalized.includes('ogg')) return 'ogg'
  if (normalized.includes('wav')) return 'wav'
  if (normalized.includes('mpeg')) return 'mp3'
  return 'webm'
}

function stopTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop()
}

function microphoneError(error: unknown): VoiceInputError {
  if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
    return new VoiceInputError('PERMISSION_DENIED')
  }
  return new VoiceInputError('RECORDING_FAILED')
}

export async function recordAudioWithMediaRecorder(maxRecordingMs = DEFAULT_RECORDING_MS): Promise<RecordedAudio> {
  const Recorder = mediaRecorderConstructor()
  if (!Recorder || !isMediaRecorderSupported()) throw new VoiceInputError('UNSUPPORTED')

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
      video: false,
    })
  } catch (error) {
    throw microphoneError(error)
  }

  const mimeType = preferredMimeType(Recorder)
  let recorder: MediaRecorder
  try {
    recorder = mimeType ? new Recorder(stream, { mimeType }) : new Recorder(stream)
  } catch {
    stopTracks(stream)
    throw new VoiceInputError('RECORDING_FAILED')
  }

  return new Promise<RecordedAudio>((resolve, reject) => {
    const chunks: Blob[] = []
    let settled = false
    let stopTimer: ReturnType<typeof setTimeout> | undefined
    let recordingStartedAt = 0

    const cleanup = () => {
      if (stopTimer !== undefined) clearTimeout(stopTimer)
      stopTracks(stream)
    }

    const rejectOnce = (error: VoiceInputError) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    }
    recorder.onerror = () => rejectOnce(new VoiceInputError('RECORDING_FAILED'))
    recorder.onstop = () => {
      if (settled) return
      settled = true
      cleanup()
      const recordedMimeType = recorder.mimeType || mimeType || chunks[0]?.type || 'audio/webm'
      const audio = new Blob(chunks, { type: recordedMimeType })
      resolve({ audio, durationMs: Math.max(0, Math.round(monotonicNow() - recordingStartedAt)) })
    }

    try {
      recordingStartedAt = monotonicNow()
      recorder.start(250)
      stopTimer = setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop()
      }, maxRecordingMs)
    } catch {
      rejectOnce(new VoiceInputError('RECORDING_FAILED'))
    }
  })
}

export class WhisperVoiceInputProvider implements VoiceInputProvider {
  private readonly endpoint: string
  private readonly batchId: string
  private readonly fetcher: Fetcher
  private readonly recordAudio: AudioRecorder
  private readonly supportCheck: () => boolean
  private readonly debug: boolean
  private readonly onDiagnostic?: DiagnosticLogger

  constructor(options: WhisperVoiceInputProviderOptions = {}) {
    this.endpoint = options.endpoint ?? DEFAULT_STT_ENDPOINT
    this.batchId = options.batchId?.trim() || DEFAULT_STT_BATCH_ID
    this.fetcher = options.fetcher ?? fetch
    this.recordAudio = options.recordAudio ?? (() => recordAudioWithMediaRecorder())
    this.supportCheck = options.supportCheck ?? isMediaRecorderSupported
    this.debug = options.debug ?? Boolean(options.onDiagnostic)
    this.onDiagnostic = options.onDiagnostic
  }

  get available(): boolean {
    return this.supportCheck()
  }

  async listen(_language: Language): Promise<string> {
    // Deliberately do not send a language hint: Whisper should auto-detect
    // multilingual and code-switched speech such as "Take me to 명동".
    if (!this.available) throw new VoiceInputError('UNSUPPORTED')

    let audio: Blob
    let recordingDurationMs = 0
    const recordingStartedAt = monotonicNow()
    try {
      const recorded = await this.recordAudio()
      if (recorded instanceof Blob) {
        audio = recorded
        recordingDurationMs = Math.max(0, Math.round(monotonicNow() - recordingStartedAt))
      } else {
        audio = recorded.audio
        recordingDurationMs = recorded.durationMs
      }
    } catch (error) {
      this.emitDiagnostic('media_recorder_failed', {
        durationMs: Math.max(0, Math.round(monotonicNow() - recordingStartedAt)),
        errorCode: error instanceof VoiceInputError ? error.code : 'UNKNOWN',
      })
      if (error instanceof VoiceInputError) throw error
      throw new VoiceInputError('RECORDING_FAILED')
    }

    this.emitDiagnostic('media_recorder_complete', {
      mimeType: audio.type || 'unknown',
      bytes: audio.size,
      durationMs: recordingDurationMs,
      empty: audio.size === 0,
      tooShort: recordingDurationMs < 300,
    })

    if (audio.size === 0) throw new VoiceInputError('ENDED')
    if (audio.size > MAX_CLIENT_AUDIO_BYTES) throw new VoiceInputError('RECORDING_FAILED')

    const formData = new FormData()
    formData.append('audio', audio, `voice.${fileExtensionForMimeType(audio.type)}`)
    formData.set('batchId', this.batchId)
    const requestEndpoint = this.debug
      ? `${this.endpoint}${this.endpoint.includes('?') ? '&' : '?'}debug=1`
      : this.endpoint
    this.emitDiagnostic('stt_upload_started', {
      endpoint: requestEndpoint,
      mimeType: audio.type || 'unknown',
      bytes: audio.size,
    })

    let response: Response
    try {
      response = await this.fetcher(requestEndpoint, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      })
    } catch {
      this.emitDiagnostic('stt_network_failed', { endpoint: requestEndpoint })
      throw new VoiceInputError('NETWORK_FAILED')
    }

    const data = await response.json().catch(() => ({})) as SttResponse
    this.forwardServerDiagnostics(data.debug)
    const upstreamCode = typeof data.code === 'string' ? data.code : undefined
    this.emitDiagnostic('stt_response_received', {
      httpStatus: response.status,
      ok: response.ok,
      code: upstreamCode ?? null,
    })
    if (!response.ok) throw new VoiceInputError('TRANSCRIPTION_FAILED', upstreamCode)

    const transcript = typeof data.transcript === 'string' ? data.transcript.trim() : ''
    if (!transcript) throw new VoiceInputError('ENDED', upstreamCode)
    return transcript
  }

  private emitDiagnostic(stage: string, details: Record<string, unknown>): void {
    if (!this.debug) return
    this.onDiagnostic?.({ source: 'client', stage, details })
  }

  private forwardServerDiagnostics(value: unknown): void {
    if (!this.debug || !value || typeof value !== 'object') return
    const events = (value as { events?: unknown }).events
    if (!Array.isArray(events)) return
    for (const event of events) {
      if (!event || typeof event !== 'object') continue
      const candidate = event as { stage?: unknown; details?: unknown; timestamp?: unknown }
      if (typeof candidate.stage !== 'string' || !candidate.details || typeof candidate.details !== 'object') continue
      this.onDiagnostic?.({
        source: 'server',
        stage: candidate.stage,
        details: candidate.details as Record<string, unknown>,
        ...(typeof candidate.timestamp === 'string' ? { timestamp: candidate.timestamp } : {}),
      })
    }
  }
}
